import P from '@hero/ui-kit/typography/P';
import React from 'react';
import Section from '@hero/ui-kit/layout/Section';
import Button from '@hero/ui-kit/inputs/Button';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import * as Style from './style.module.scss';
import getApprovalStatus from '../utils/getApprovalStatus';
import { AllApprovalStatusSubStates, ApprovalStatus, LeadActivityDetail } from '../types';
import getApprovalSubStatus from '../utils/getApprovalSubStatus';
import { format, isValid } from 'date-fns';
import RefreshIcon from '../../../icons/RefreshIcon';
import useUpdateStatusChangeNote from '../api/useUpdateStatusChangeNote';

// HWM-22633 round 4b: `LeadActivity` is the legacy per-status row visual.
// Round 4b extends it with inline note CRUD attached below each row:
// NOTE block + Edit / Delete buttons when present, "Add a note" pill
// when absent, inline textarea + Cancel / Save when editing. Wired to
// `useUpdateStatusChangeNote` via the new `historicalId` prop (the
// historical PatientLead row PK that the round-4 endpoint targets).

interface LeadActivityProps {
    activityDetails: LeadActivityDetail & { historicalId?: number };
    isReactivated?: boolean;
    leadId?: string;
    canEdit?: boolean;
}

const LeadActivity: React.FC<LeadActivityProps> = ({
    activityDetails,
    isReactivated = false,
    leadId,
    canEdit = false
}) => {
    const approvalStatus = activityDetails?.status
        ? (activityDetails.status.toUpperCase() as ApprovalStatus)
        : undefined;
    const approvalSubStatus = activityDetails?.sub_status
        ? (activityDetails.sub_status.toUpperCase() as AllApprovalStatusSubStates)
        : undefined;
    // HWM-22751: DrChrono-sync entries render as "DrChrono sync" instead of an
    // agent name. Unrecognized `source` values fall through to `updated_by` for
    // forward-compat with future inbound integrations.
    const name =
        'source' in activityDetails && activityDetails.source === 'drchrono'
            ? 'DrChrono sync'
            : activityDetails.updated_by;
    // Thu, June 29, 2023 at 09:52 AM
    const updatedAt =
        activityDetails.updated_at && isValid(new Date(activityDetails.updated_at))
            ? new Date(activityDetails.updated_at)
            : undefined;
    const date = updatedAt
        ? format(
              new Date(
                  Date.UTC(
                      updatedAt.getFullYear(),
                      updatedAt.getMonth(),
                      updatedAt.getDate(),
                      updatedAt.getHours(),
                      updatedAt.getMinutes(),
                      updatedAt.getSeconds()
                  )
              ),
              'PPPPp'
          )
        : 'Invalid date';
    const status = activityDetails?.sub_status
        ? getApprovalSubStatus(approvalSubStatus, approvalStatus)
        : getApprovalStatus(approvalStatus);

    const scheduleDate =
        activityDetails?.note_datetime && isValid(new Date(activityDetails?.note_datetime))
            ? new Date(activityDetails?.note_datetime)
            : undefined;

    const preparedScheduledDate = scheduleDate
        ? new Date(
              Date.UTC(
                  scheduleDate.getFullYear(),
                  scheduleDate.getMonth(),
                  scheduleDate.getDate(),
                  scheduleDate.getHours(),
                  scheduleDate.getMinutes(),
                  scheduleDate.getSeconds()
              )
          )
        : undefined;

    const labelStyles: React.CSSProperties = {
        ...(status.label.toLowerCase() === 'enrolled' && { color: '#74a305' }),
        marginTop: '0.4rem'
    };

    // HWM-22633 round 4b: inline note CRUD state.
    const historicalId = activityDetails.historicalId;
    const canEditNote = Boolean(canEdit && leadId && historicalId !== undefined);
    const [isEditing, setIsEditing] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    const closeEditor = React.useCallback(() => {
        setIsEditing(false);
        setDraft('');
    }, []);
    const { mutateAsync, isPending } = useUpdateStatusChangeNote(closeEditor);

    const startEditing = () => {
        setDraft(activityDetails.note ?? '');
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!canEditNote || historicalId === undefined || !leadId) return;
        try {
            await mutateAsync({
                leadId,
                historicalId,
                note: draft.trim() || null
            });
        } catch {
            /* surfaced via notification context */
        }
    };

    const handleDelete = async () => {
        if (!canEditNote || historicalId === undefined || !leadId) return;
        try {
            await mutateAsync({ leadId, historicalId, note: null });
        } catch {
            /* surfaced via notification context */
        }
    };

    const hasNote = Boolean(activityDetails.note);

    return (
        <>
            <div className={Style.leadActivityItem}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        columnGap: '1rem',
                        marginBottom: '1.6rem'
                    }}
                >
                    {isReactivated ? (
                        <Section
                            styles={{
                                background: '#ffede1',
                                padding: '0.8rem 1.2rem',
                                borderRadius: '8px'
                            }}
                            noDefaultPadding
                        >
                            <div
                                style={{ display: 'flex', alignItems: 'center', columnGap: '1rem' }}
                            >
                                <RefreshIcon type="utility" width={16} height={16} />
                                <P noDefaultMargin size="small" styles={{ fontWeight: 600 }}>
                                    Reactivated
                                </P>
                            </div>
                        </Section>
                    ) : null}
                    <P noDefaultMargin>
                        {name ? (
                            <span style={{ color: '#444444', fontWeight: 400 }}>{name}</span>
                        ) : (
                            <i>{`Automatically created via website form`}</i>
                        )}
                        <span style={{ fontSize: '1.4rem', color: '#777777' }}>{` • ${date}`}</span>
                    </P>
                </div>

                <P noDefaultMargin strong styles={labelStyles}>
                    {status.label}
                </P>
                {preparedScheduledDate ? (
                    <div className={Style.scheduleDateWrapper}>
                        <P noDefaultMargin className={Style.scheduleDate}>
                            {format(preparedScheduledDate, 'EEE, MMMM d')}
                        </P>
                        <P noDefaultMargin className={Style.scheduleDate}>
                            {format(preparedScheduledDate, 'p')}
                        </P>
                    </div>
                ) : null}

                {/* HWM-22633 round 4b: per-row note CRUD. UI-Kit primitives
                    (ClickableOpacity for the inline link buttons,
                    Button for Cancel/Save, native <textarea> matches the
                    existing NoteModal pattern). */}
                {hasNote && !isEditing ? (
                    <div className={Style.leadActivityNoteBlock}>
                        <div className={Style.leadActivityNoteHeader}>
                            <P size="small" noDefaultMargin className={Style.leadActivityNoteLabel}>
                                Note
                            </P>
                            {canEditNote ? (
                                <div className={Style.leadActivityNoteHeaderActions}>
                                    <ClickableOpacity
                                        alt="Edit note"
                                        onClick={startEditing}
                                        disabled={isPending}
                                    >
                                        <span className={Style.leadActivityNoteEditButton}>
                                            Edit
                                        </span>
                                    </ClickableOpacity>
                                    <ClickableOpacity
                                        alt="Delete note"
                                        onClick={handleDelete}
                                        disabled={isPending}
                                    >
                                        <span className={Style.leadActivityNoteDeleteButton}>
                                            {isPending ? 'Deleting…' : 'Delete'}
                                        </span>
                                    </ClickableOpacity>
                                </div>
                            ) : null}
                        </div>
                        <P noDefaultMargin className={Style.leadActivityNoteBody}>
                            {activityDetails.note}
                        </P>
                    </div>
                ) : null}

                {!hasNote && !isEditing && canEditNote ? (
                    <div className={Style.leadActivityAddNoteWrapper}>
                        <Button
                            variant="outlineSecondary"
                            size="small"
                            onClick={startEditing}
                            disabled={isPending}
                        >
                            Add a note
                        </Button>
                    </div>
                ) : null}

                {isEditing
                    ? (() => {
                          // Disable Save when nothing has changed OR when the
                          // user has cleared the textarea entirely. Round-4b
                          // review feedback: an empty-trim Save would silently
                          // clear an existing note, one click from accidental
                          // destruction — force the user through the explicit
                          // Delete button for that path.
                          const trimmed = draft.trim();
                          const original = activityDetails.note ?? '';
                          const isUnchanged = trimmed === original.trim();
                          const isEmpty = trimmed.length === 0;
                          const saveDisabled = isPending || isEmpty || isUnchanged;
                          return (
                              <div className={Style.leadActivityNoteEditor}>
                                  <textarea
                                      value={draft}
                                      onChange={(e) => setDraft(e.target.value)}
                                      rows={3}
                                      autoFocus
                                      className={Style.leadActivityNoteTextarea}
                                      placeholder="Add a note…"
                                  />
                                  <div className={Style.leadActivityNoteEditorActions}>
                                      <Button
                                          variant="outlineSecondary"
                                          size="small"
                                          onClick={closeEditor}
                                          disabled={isPending}
                                      >
                                          Cancel
                                      </Button>
                                      <Button
                                          size="small"
                                          onClick={handleSave}
                                          disabled={saveDisabled}
                                      >
                                          {isPending ? 'Saving…' : 'Save'}
                                      </Button>
                                  </div>
                              </div>
                          );
                      })()
                    : null}
            </div>
        </>
    );
};

export default LeadActivity;
